import { gsap } from 'gsap'

export const EASE = Object.freeze({
  reveal: 'expo.out',      // 显现
  glide: 'power4.out',     // 位移滑行
  exit: 'power2.in',       // 退场
  swap: 'power2.inOut',    // 双向交接
  settle: 'back.out(1.6)', // 落定确认
})

export const DUR = Object.freeze({
  press: 0.14,
  release: 0.46,
  hoverIn: 0.34,
  hoverOut: 0.42,
  slide: 0.52,
  pageOut: 0.18,
  pageIn: 0.62,
  overlayIn: 0.42,
  overlayOut: 0.2,
})

const reducedQuery = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null

export function prefersReducedMotion() {
  return Boolean(reducedQuery?.matches)
}

function skipMotion() {
  return prefersReducedMotion() || (typeof document !== 'undefined' && document.hidden)
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) return
    gsap.globalTimeline.getChildren(false, true, true).forEach(t => t.progress(1, false))
  })
}

const PRESSABLE = [
  '.el-button', '.btn', '.icon-btn', '.nav-item', '.segmented button', '.pg-btn',
  '.perm-chip', '.cmd-trigger', '.user-chip', '.quick-link', '.menu-pop .menu-item',
].join(', ')

let pressInstalled = false

export function installPressFeedback() {
  if (pressInstalled || typeof window === 'undefined') return
  pressInstalled = true
  const pressed = new Set<Element>()

  const release = () => {
    pressed.forEach(el => gsap.to(el, { scale: 1, duration: DUR.release, ease: EASE.settle, overwrite: 'auto' }))
    pressed.clear()
  }

  document.addEventListener('pointerdown', (event) => {
    if (skipMotion() || event.button !== 0) return
    const el = (event.target as Element | null)?.closest(PRESSABLE)
    if (!el || (el as HTMLButtonElement).disabled) return
    pressed.add(el)
    gsap.to(el, { scale: 0.955, duration: DUR.press, ease: 'power2.out', overwrite: 'auto' })
  }, { passive: true })
  window.addEventListener('pointerup', release, { passive: true })
  window.addEventListener('pointercancel', release, { passive: true })
}

/** 滑块沿目标元素滑动（指示器药丸）：支持 x / y 轴 */
export function slideThumb(
  thumb: HTMLElement | null,
  target: HTMLElement | null,
  axis: 'x' | 'y' = 'x',
  instant = false,
) {
  if (!thumb) return
  if (!target) {
    gsap.set(thumb, { opacity: 0 })
    return
  }
  const parent = thumb.parentElement
  if (!parent) return
  const pr = parent.getBoundingClientRect()
  const tr = target.getBoundingClientRect()
  const kx = parent.offsetWidth ? pr.width / parent.offsetWidth : 1
  const ky = parent.offsetHeight ? pr.height / parent.offsetHeight : 1
  const props = axis === 'y'
    ? { y: (tr.top - pr.top) / ky - parent.clientTop + parent.scrollTop, height: tr.height / ky, opacity: 1 }
    : { x: (tr.left - pr.left) / kx - parent.clientLeft + parent.scrollLeft, width: tr.width / kx, opacity: 1 }
  if (instant || skipMotion()) {
    gsap.set(thumb, props)
    return
  }
  gsap.to(thumb, { ...props, duration: DUR.slide, ease: EASE.glide, overwrite: true })
}

/** 页面离场过渡 */
export function pageLeave(el: Element, done: () => void) {
  if (skipMotion()) {
    setTimeout(done, 0)
    return
  }
  gsap.to(el, {
    opacity: 0,
    scale: 0.988,
    duration: DUR.pageOut,
    ease: EASE.exit,
    onComplete: done,
  })
}

/** 页面入场过渡 */
export function pageEnter(el: Element, done: () => void) {
  const page = el as HTMLElement
  if (skipMotion()) {
    page.classList.add('is-entrance-ready')
    return done()
  }
  const items = page.querySelectorAll('.reveal')
  gsap.set(page, { opacity: 1, scale: 0.994 })
  gsap.set(items, { opacity: 0, y: 12 })
  page.classList.add('is-entrance-ready')
  const tl = gsap.timeline({ onComplete: () => { gsap.set(page, { clearProps: 'transform' }); done() } })
  tl.to(page, { scale: 1, duration: DUR.pageIn, ease: EASE.glide }, 0)
  if (items.length) {
    tl.to(items, { opacity: 1, y: 0, duration: DUR.pageIn, ease: EASE.reveal, stagger: { each: 0.05, from: 'start' }, clearProps: 'transform' }, 0.04)
  }
}
